import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { environment } from '../../../environments/environment.development';

declare global {
  interface Window {
    __reCaptchaOnLoad?: () => void;
    grecaptcha?: any;
  }
}

@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private renderer: Renderer2;
  private widgetId: number | null = null;
  private readyPromise: Promise<void> | null = null;

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  private loadScript(): Promise<void> {
    if (this.readyPromise) return this.readyPromise;

    this.readyPromise = new Promise((resolve, reject) => {
      if (window.grecaptcha) {
        return resolve();
      }

      window.__reCaptchaOnLoad = () => resolve();

      const script = this.renderer.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=__reCaptchaOnLoad&render=explicit';
      script.async = true;
      script.defer = true;
      script.onerror = (err: any) => reject(err);
      this.renderer.appendChild(document.head, script);
    });

    return this.readyPromise;
  }

  async render(container: HTMLElement): Promise<void> {
    await this.loadScript();
    if (!window.grecaptcha) throw new Error('grecaptcha not available');

    if (this.widgetId === null) {
      this.widgetId = window.grecaptcha.render(container, {
        sitekey: environment.recaptchaSiteKey,
        size: 'invisible'
      });
    }
  }

  async execute(): Promise<string> {
    if (this.widgetId === null) throw new Error('Recaptcha not rendered');
    return await window.grecaptcha.execute(this.widgetId);
  }

  reset() {
    if (this.widgetId !== null && window.grecaptcha) {
      window.grecaptcha.reset(this.widgetId);
    }
  }
}
